import React from 'react';
import { Box } from '@mui/material';
import S from './PlanningVue.module.scss';
import FormulairePlanningException from './FormulairePlanningException';
import TableauPlanningExceptionnel from './TableauPlanningExceptionnel';
import Timeline from './components/Timeline';
import { Task } from 'types/types';
import { Mode } from './types';
import {
  getDatesBetweenDates,
  filterExceptionTasks,
} from './PlanningVue.utils';

interface PlanningVueTabsProps {
  tabValue: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  defaultModes: Mode[];
  selectedMode: Mode | undefined;
  currentInteractionMode: string;
  setCurrentInteractionMode: (mode: string) => void;
  timelineMatrix: Mode[][];
  markerMatrix: number[][];
  exTimelineMatrix: Mode[][] | undefined;
  exMarkerMatrix: number[][] | undefined;
  handleTimelineMatrix: (dayIndex: number, newTimeline: Mode[]) => void;
  handleMarkerMatrix: (dayIndex: number, newMarkers: number[]) => void;
  onDayClick: (dayIndex: number) => void;
  tasks: Task[];
  exceptionPlanningInfo: { title: string; startDate: Date; endDate: Date };
  setExPlanningInfo: (data: {
    title: string;
    startDate: Date;
    endDate: Date;
  }) => void;
  unavailableDatesPeriodes: Date[];
  displayExPlanning: (e: any) => void;
  removeExceptionPlanning: (e: any) => void;
  sourceDayForCopy: number | null;
  targetDaysForCopy: number[];
  a11yProps: (index: number) => any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PlanningVueTabs: React.FC<PlanningVueTabsProps> = ({
  tabValue,
  onTabChange,
  defaultModes,
  selectedMode,
  currentInteractionMode,
  setCurrentInteractionMode,
  timelineMatrix,
  markerMatrix,
  exTimelineMatrix,
  exMarkerMatrix,
  handleTimelineMatrix,
  handleMarkerMatrix,
  onDayClick,
  tasks,
  exceptionPlanningInfo,
  setExPlanningInfo,
  unavailableDatesPeriodes,
  displayExPlanning,
  removeExceptionPlanning,
  sourceDayForCopy,
  targetDaysForCopy,
  a11yProps,
}) => {
  const renderTimelines = () => {
    if (!exceptionPlanningInfo.title) {
      return [...Array(7)].map((_, i) => {
        const dayIndex = i === 6 ? 0 : i + 1;
        const isSourceDay = sourceDayForCopy === dayIndex;
        const isTargetDay = targetDaysForCopy.includes(dayIndex);
        const isInCopyMode = currentInteractionMode === 'copy-day';

        return (
          <Timeline
            key={dayIndex}
            selectedMode={selectedMode}
            currentInteractionMode={currentInteractionMode}
            setCurrentInteractionMode={setCurrentInteractionMode}
            dayOfWeek={dayIndex}
            handleMarkerMatrix={handleMarkerMatrix}
            handleTimelineMatrix={handleTimelineMatrix}
            initialSegments={timelineMatrix[dayIndex]}
            initialMarkers={markerMatrix[dayIndex]}
            onDayClick={() => onDayClick(dayIndex)}
            isSourceDay={isSourceDay}
            isTargetDay={isTargetDay}
            isInCopyMode={isInCopyMode}
          />
        );
      });
    } else {
      const numberOfDays = getDatesBetweenDates(
        exceptionPlanningInfo.startDate,
        exceptionPlanningInfo.endDate
      );
      return numberOfDays.map((date: Date, i: number) => {
        const isSourceDay = sourceDayForCopy === i;
        const isTargetDay = targetDaysForCopy.includes(i);
        const isInCopyMode = currentInteractionMode === 'copy-day';
        return (
          <Timeline
            key={i}
            selectedMode={selectedMode}
            currentInteractionMode={currentInteractionMode}
            setCurrentInteractionMode={setCurrentInteractionMode}
            dayOfWeek={i}
            dayDate={numberOfDays[i]}
            handleMarkerMatrix={handleMarkerMatrix}
            handleTimelineMatrix={handleTimelineMatrix}
            initialSegments={exTimelineMatrix![i]}
            initialMarkers={exMarkerMatrix![i]}
            onDayClick={() => onDayClick(i)}
            isSourceDay={isSourceDay}
            isTargetDay={isTargetDay}
            isInCopyMode={isInCopyMode}
          />
        );
      });
    }
  };

  return (
    <div className={[S.dayBox, S.modesBox, S.leftColumn].join(' ')}>
      <CustomTabPanel value={tabValue} index={0}>
        {renderTimelines()}
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={1}>
        {filterExceptionTasks(tasks).length > 0 && (
          <div style={{ marginBottom: '15px' }} className={S.infoNote}>
            <span className={S.infoIcon}>i</span>
            <span className={S.infoText}>
              Les plannings d'exception ne prennent en compte que les capteurs
              en Mode Plannifié
            </span>
          </div>
        )}
        <TableauPlanningExceptionnel
          selectioneLigne={(e: any) => displayExPlanning(e)}
          exPlannings={filterExceptionTasks(tasks)}
          onDelete={(row: any) => removeExceptionPlanning(row)}
          onView={(row: any) => displayExPlanning(row)}
        />
        <button
          onClick={(e) => onTabChange(e, 2)}
          className={S.addExceptionButton}
        >
          Créer un nouveau planning d'exception
        </button>
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={2}>
        <FormulairePlanningException
          changerOnglet={onTabChange}
          setExPlanningInfo={setExPlanningInfo}
          unavailableDatesPeriodes={unavailableDatesPeriodes}
        />
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={3}>
        {renderTimelines()}
        {exceptionPlanningInfo.title && (
          <button
            className={S.cancelExceptionButton}
            onClick={() => {
              setExPlanningInfo({
                title: '',
                startDate: new Date(),
                endDate: new Date(),
              });
              onTabChange(null as any, 1);
            }}
          >
            Annuler
          </button>
        )}
      </CustomTabPanel>
    </div>
  );
};

export default PlanningVueTabs;
